/**
 * The reply invitation: when the zero-reply composer prompt shows.
 *
 * Design: `designs/01-now/2026-07-28-reply-invitation-norm-line-and-zero-reply-composer-design.md`.
 *
 * SHIPPED TO 100% on 2026-09-12 (operator decision 2026-09-09, SV-46; slice `1f`). From 2026-07-29 to
 * 2026-09-08 this file also held the experiment arm, `isReplyInvitationTreated`, which showed the norm
 * line (T1) and this prompt (T2) only on discussions whose `public_id` character-code sum was odd. The
 * readout moved the aggregate +6.3% relative (p=0.077) and single-post authors +27.0% relative, and the
 * operator shipped both treatments to every discussion without building an experiment framework (no
 * `ExperimentService`, no `experiment_assignment`). The arm is deleted; T1 and T2 are unconditional.
 *
 * Kept as a HISTORICAL readout aid, not a live gate: the experiment period's split can still be
 * recomputed in Postgres with
 *
 *     (SELECT sum(ascii(c)) FROM regexp_split_to_table(d.public_id, '') AS c) % 2
 *
 * (1 = treated). It is meaningful only for discussions viewed between 2026-07-29 and the deploy that
 * carries `1f`; after that every discussion is treated.
 */

/** Hours after publication during which the zero-reply prompt still reads as "early", not "ignored". */
export const ZERO_REPLY_PROMPT_MAX_AGE_HOURS = 72;

/**
 * True when the composer should say "no one has answered this yet".
 *
 * Both halves matter. On a post that already has replies the claim is simply false; on a three-week-old
 * post it stops reading as "be first" and starts reading as "nobody cared", which is a discouragement.
 * `ZERO_REPLY_PROMPT_MAX_AGE_HOURS` is what keeps it a signal of earliness.
 *
 * `visibleReplyCount` must be the count the READER can see. business_plan's `AGENTS.md` §2b warns that
 * counting `public.thoughts` alone understates a post's real comment load because scheduled comments
 * sit in `thought_publication_requests` — that is correct for their eligibility maths and wrong here,
 * because this copy makes a claim about what is on screen. Do not "fix" it to include the queue.
 */
export function shouldShowZeroReplyPrompt(
    visibleReplyCount: number,
    discussionCreatedAt: string | undefined | null,
    now: Date = new Date(),
): boolean {
    if (visibleReplyCount > 0 || !discussionCreatedAt) {
        return false;
    }
    const createdAtMs = Date.parse(discussionCreatedAt);
    if (Number.isNaN(createdAtMs)) {
        return false;
    }
    const ageHours = (now.getTime() - createdAtMs) / (1000 * 60 * 60);
    return ageHours >= 0 && ageHours < ZERO_REPLY_PROMPT_MAX_AGE_HOURS;
}
